"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, RotateCw, Waves, Droplet, Gauge, ArrowRight, CircleQuestionMark as HelpCircle, PhoneCall } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
interface ProblemItem {
  id: string;
  iconName: "flush" | "running" | "overflow" | "leak" | "fill";
  title: string;
  tag: string;
  tagVariant: "default" | "secondary" | "destructive" | "outline";
  description: string;
  actionHint: string;
}
const toiletIssues: ProblemItem[] = [{
  id: "flush",
  iconName: "flush",
  title: "Toilet Won't Flush",
  tag: "High Priority",
  tagVariant: "secondary",
  description: "Handle feels loose, flapper is disconnected, or the lift chain has slipped inside the tank.",
  actionHint: "Check handle connection or clear internal flapper blockages."
}, {
  id: "running",
  iconName: "running",
  title: "Toilet Keeps Running",
  tag: "Water Waste",
  tagVariant: "outline",
  description: "Worn-out flapper seal or misaligned fill valve causing continuous water flow and high utility bills.",
  actionHint: "Inspect tank water level and replace degraded rubber seals."
}, {
  id: "overflow",
  iconName: "overflow",
  title: "Toilet Won't Stop Overflowing",
  tag: "Urgent Emergency",
  tagVariant: "destructive",
  description: "Severe main clog preventing water drainage, causing rapid bowl rise and water spillover onto floors.",
  actionHint: "Shut off the supply valve behind the toilet immediately."
}, {
  id: "leak",
  iconName: "leak",
  title: "Toilet Leaking at Base or Tank",
  tag: "Structural Risk",
  tagVariant: "secondary",
  description: "Failed wax ring seal at the floor level or cracked tank bolts creating hidden pooling and subfloor rot.",
  actionHint: "Avoid use to prevent ceiling and floor foundation damage."
}, {
  id: "fill",
  iconName: "fill",
  title: "Toilet Won't Fill or Fills Slowly",
  tag: "Pressure Issue",
  tagVariant: "outline",
  description: "Clogged supply line, closed shutoff valve, or mineral build-up restricting water flow to the tank.",
  actionHint: "Verify the angle stop valve is open and clear of debris."
}];
export default function ToiletProblems() {
  const prefersReducedMotion = useReducedMotion();
  const getProblemIcon = (iconName: ProblemItem["iconName"]) => {
    switch (iconName) {
      case "flush":
        return <RotateCw className="size-5 text-primary" />;
      case "running":
        return <Waves className="size-5 text-primary" />;
      case "overflow":
        return <AlertTriangle className="size-5 text-destructive" />;
      case "leak":
        return <Droplet className="size-5 text-primary" />;
      case "fill":
        return <Gauge className="size-5 text-primary" />;
      default:
        return <HelpCircle className="size-5 text-primary" />;
    }
  };
  return <section id="toiletproblems" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-colors duration-200">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3 font-medium px-3 py-1">
            Emergency Triage Guide
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Toilet Problems
          </h2>
          <p className="text-base md:text-lg text-muted-foreground">
            Identify your bathroom issue quickly. From minor valve repairs to urgent overflow emergencies in Jonesboro, find out what is causing the breakdown and how to address it.
          </p>
        </div>

        {/* Diagnostic Problem Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {toiletIssues.map((problem, i) => <motion.div key={problem.id} data-index={i} initial={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.4,
          delay: prefersReducedMotion ? 0 : i * 0.08
        }} className={`flex flex-col ${i === 4 ? "md:col-span-2 lg:col-span-1" : ""}`}>
              <Card className="h-full border border-border bg-card text-card-foreground hover:shadow-md transition-[box-shadow,border-color] duration-200 flex flex-col justify-between">
                <div>
                  <CardHeader className="space-y-3 pb-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="size-11 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                        {getProblemIcon(problem.iconName)}
                      </div>
                      <Badge variant={problem.tagVariant} className="text-xs">
                        {problem.tag}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-default font-bold text-card-foreground pt-1">
                      {problem.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 pt-0">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {problem.description}
                    </CardDescription>
                  </CardContent>
                </div>
                <div className="px-6 pb-6 pt-2 border-t border-border/50 mt-auto">
                  <p className="text-xs font-medium text-foreground flex items-center gap-1.5">
                    <span className="text-accent-foreground font-semibold">Diagnosis Tip:</span>
                    <span className="text-muted-foreground">{problem.actionHint}</span>
                  </p>
                </div>
              </Card>
            </motion.div>)}
        </div>

        {/* Action Area */}
        <div className="mt-12 md:mt-16 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-6 bg-secondary/30 rounded-2xl p-6 sm:p-8">
          <div className="text-center sm:text-left space-y-1">
            <h3 className="text-lg md:text-xl font-default font-bold text-foreground">
              Need on-site toilet repair in Jonesboro?
            </h3>
            <p className="text-sm text-muted-foreground">
              Our licensed plumbers provide round-the-clock emergency diagnostics and fast component replacements.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto font-semibold">
              <Link to="/toilet-repair/" className="inline-flex items-center gap-2">
                <span>View Toilet Problems</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}